import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    NgbPopover,
    NgbPopoverModule,
    PlacementArray
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { AncDateRangeComponent } from './anc-date-range/anc-date-range.component';
import {
    AncDateRangeValue,
    AncFilterChip,
    AncFilterOption,
    AncFilterChipSize
} from './anc-filter.models';
import { AncButtonDirective } from '../anc-button/anc-button.directive';

@Component({
    selector: 'app-anc-filter-chip',
    imports: [
        CommonModule,
        FormsModule,
        NgbPopoverModule,
        TranslateModule,
        AncDateRangeComponent,
        AncButtonDirective
    ],
    templateUrl: './anc-filter-chip.component.html',
    styleUrls: ['./anc-filter-chip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncFilterChipComponent implements OnChanges {
    @Input() chip!: AncFilterChip;
    /**
     * Popover placement(s). Default below the pill. In a grid cell pass
     * ['bottom-start', 'top-start']: it opens upwards when there is no room below,
     * so the panel does not make the page scroll.
     */
    @Input() placement: PlacementArray = 'bottom-start';
    @Input() value: any = null;
    @Input() options: AncFilterOption[] = [];
    @Input() optionsLoading: boolean = false;
    @Input() hasMoreOptions: boolean = false;
    @Input() disabled: boolean = false;
    /**
     * Figma size variant. Defaults to `mini` for grid filter bars.
     * `default` = 42px, `compact` = 40px, `mini` = 32px.
     */
    @Input() size: AncFilterChipSize = 'mini';

    @Output() open = new EventEmitter<void>();
    @Output() valueChange = new EventEmitter<any>();
    @Output() loadMore = new EventEmitter<void>();

    /** Draft = transient UI state inside the popover. Committed on Apply. */
    draftIds: Set<string | number> = new Set();
    searchTerm: string = '';
    popoverOpen: boolean = false;

    @ViewChild('chipPopover') chipPopover?: NgbPopover;

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']) this.syncDraftFromValue();
    }

    get applied(): boolean {
        if (this.value == null) return false;
        if (Array.isArray(this.value)) return this.value.length > 0;
        if (typeof this.value === 'object')
            return Object.keys(this.value).length > 0;
        return this.value !== '' && this.value !== false;
    }

    get showCaret(): boolean {
        return this.chip.showCaret ?? this.chip.type !== 'toggle';
    }

    get isMultiValue(): boolean {
        return Array.isArray(this.value) && this.value.length > 0;
    }

    get displayLabel(): string {
        if (this.applied && this.chip.formatValue)
            return this.chip.formatValue(this.value);
        if (this.applied && Array.isArray(this.value)) {
            return `${this.chip.label} · ${this.value.length}`;
        }
        return this.chip.label;
    }

    get visibleOptions(): AncFilterOption[] {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) return this.options;
        return this.options.filter((o) => o.label.toLowerCase().includes(term));
    }

    onClick(): void {
        if (this.disabled) return;
        if (this.chip.type === 'toggle') {
            this.valueChange.emit(!this.value);
        }
        // For non-toggle chips, ngbPopover handles open via [ngbPopover]/click.
    }

    onPopoverShown(): void {
        this.popoverOpen = true;
        this.syncDraftFromValue();
        this.open.emit();
    }

    onPopoverHidden(): void {
        this.popoverOpen = false;
        this.searchTerm = '';
    }

    isDraftSelected(opt: AncFilterOption): boolean {
        return this.draftIds.has(opt.id);
    }

    toggleDraft(opt: AncFilterOption): void {
        if (this.draftIds.has(opt.id)) this.draftIds.delete(opt.id);
        else this.draftIds.add(opt.id);
        this.cdr.markForCheck();
    }

    clearDraft(): void {
        this.draftIds.clear();
        this.valueChange.emit([]);
        this.closePopover();
    }

    applyDraft(): void {
        const arr = Array.from(this.draftIds);
        this.valueChange.emit(arr);
        this.closePopover();
    }

    selectSingle(opt: AncFilterOption): void {
        this.valueChange.emit(opt);
        this.closePopover();
    }

    /** Clears a single-select chip so the field goes back to its empty state. */
    clearSingle(): void {
        this.valueChange.emit(null);
        this.closePopover();
    }

    /** Pill clear X — stop the pill click so the popover does not toggle. */
    onClearClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.clearSingle();
    }

    private closePopover(): void {
        this.chipPopover?.close();
        this.popoverOpen = false;
    }

    onDateRangeChange(value: AncDateRangeValue | null): void {
        this.valueChange.emit(value);
        this.popoverOpen = false;
    }

    private syncDraftFromValue(): void {
        this.draftIds = new Set(Array.isArray(this.value) ? this.value : []);
    }
}
